import React from 'react';
import { StatusBar, Platform } from 'react-native';
import { Interactable } from 'LocalGluons';
import Item from './Item';
import Button from './Button';
import { Device } from '../../../DeviceUtils/DeviceUtils';

// eslint-disable-next-line max-statements
export const getPosition = (buttonPosition, isAbsolute, isDisplayedInDetailsPopUp) => {
    const {
        height: pageHeight,
        width: pageWidth,
        pageX,
        pageY,
        buttonWidth,
        buttonHeight
    } = buttonPosition;
    const buttonCenter = { x: pageX + buttonWidth / 2, y: pageY + buttonHeight / 2 };
    const screenCenter = { x: pageWidth / 2, y: pageHeight / 2 };

    const position = {};
    position.width = buttonWidth;

    if (buttonCenter.x <= screenCenter.x) {
        // left
        position.left = isAbsolute ? pageX : 0;
        position.horizontal = 'left';
    }
    if (Device.isWeb && !isDisplayedInDetailsPopUp)
        position.left = pageWidth / 2 - 1.599 * buttonWidth;
    if (buttonCenter.x > screenCenter.x) {
        // right
        position.right = isAbsolute ? pageWidth - (pageX + buttonWidth) : 0;
        position.horizontal = 'right';
    }
    if (buttonCenter.y <= screenCenter.y) {
        // top
        position.top = isAbsolute ? pageY + buttonHeight : buttonHeight;
        /*
            We need to slightly modify top position on each platform.
            Probably due to different measurement of element position.
            Without this, dropdown will be either too low or too high relative to button
         */
        if (Device.isAndroid) position.top -= 2;
        if (Device.isWeb) position.top = pageY + buttonHeight;
        position.vertical = 'top';
    }
    if (buttonCenter.y > screenCenter.y) {
        // bottom
        const androidBarHeight = Platform.OS === 'android' ? StatusBar.currentHeight || 25 : 0;
        position.bottom = isAbsolute ? pageHeight - androidBarHeight - pageY : buttonHeight;
        const webButtonSpacing = 13;
        if (Device.isWeb && isDisplayedInDetailsPopUp)
            position.bottom =
                pageHeight - (buttonCenter.y + buttonHeight * 3 + webButtonSpacing * 2);
        position.vertical = 'bottom';
    }

    return position;
};

export const getIconPosition = ({ horizontal, vertical }, iconSize) => {
    const style = {};
    const iconMargin = 1;

    switch (horizontal) {
        case 'left':
            style.left = '10%';
            break;
        case 'right':
            style.left = '80%';
            break;
        default:
            break;
    }
    switch (vertical) {
        case 'top':
            style.top = -(iconSize - iconMargin);
            break;
        case 'bottom':
            style.top = '100%';
            style.transform = [{ rotateX: '180deg' }];
            break;
        default:
            break;
    }
    return style;
};

/*
 This function returns children based on data or children properties.
 The shape of children is based on presence of children and renderItem props.
*/
export const getTransformedChildren = (
    { data, renderItem, onValueChange },
    childrenArray,
    setDropdownOpen
) => {
    const onPress = (i, value) => {
        onValueChange(value, i);
        setDropdownOpen(false);
    };
    if (data) {
        return data.map((itemProps, index) =>
            renderItem ? (
                renderItem(itemProps)
            ) : (
                <Item
                    key={itemProps.value}
                    {...itemProps}
                    onPress={() => {
                        onPress(index, itemProps.value);
                    }}
                />
            )
        );
    }

    return childrenArray
        .filter((child) => child.type === Item)
        .map((child, index) => {
            const generatedKey = `${child.value || index}${child.label || index}`;
            if (child.props.children) {
                const childChild = React.Children.toArray(child.props.children)[0];
                if (childChild.props.onPress) {
                    return React.cloneElement(childChild, {
                        ...childChild.props,
                        onPress: () => {
                            childChild.props.onPress();
                            onPress(index, child.props.value);
                        },
                        key: generatedKey
                    });
                }
                return (
                    <Interactable
                        onPress={() => {
                            onPress(index, child.props.value);
                        }}
                        key={generatedKey}
                    >
                        {childChild}
                    </Interactable>
                );
            }
            return (
                <Item
                    {...child.props}
                    onPress={() => setDropdownOpen(false)}
                    onValueChange={onValueChange}
                    index={index}
                    key={generatedKey}
                />
            );
        });
};

export const getLabelValueMap = (data, childrenArray) => {
    const reducer = (acc, curr) => {
        acc[curr.value] = curr.label;
        return acc;
    };
    const flattenMap = ({ props: { label, value } }) => ({ label, value });
    const flattenData = data || childrenArray.map(flattenMap);
    return flattenData.reduce(reducer, {});
};

export const getDefaultLabel = ({ data, children }) =>
    data ? data[0].label : children?.[0]?.props.label || '';

export const createButtonComponent = (
    childrenArray,
    selectedLabel,
    setDropdownOpen,
    hasTVPreferredFocus
) => {
    const ButtonComponent = childrenArray.find((child) => child.type === Button);
    if (ButtonComponent) {
        return React.cloneElement(ButtonComponent, { setDropdownOpen });
    }
    return (
        <Button
            selectedLabel={selectedLabel}
            setDropdownOpen={setDropdownOpen}
            hasTVPreferredFocus={hasTVPreferredFocus}
        />
    );
};

export const conditionallyWrapComponent = (Component, condition, WrapperForTrue, WrapperForFalse) =>
    condition ? (
        <WrapperForTrue>
            <Component />
        </WrapperForTrue>
    ) : (
        <WrapperForFalse>
            <Component />
        </WrapperForFalse>
    );
